import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import CouponForm from './CouponForm';
import CountryManager from './CountryManager';
import BrandSettings from './BrandSettings';

const { FiPlus, FiEdit2, FiTrash2, FiX, FiSearch, FiChevronLeft, FiChevronRight, FiFilter, FiArrowLeft, FiSettings, FiGlobe, FiBriefcase } = FiIcons;

const ControlPanel = ({
  countries,
  coupons,
  brandSettings,
  onSaveCoupon,
  onDeleteCoupon,
  onAddCountry,
  onEditCountry,
  onDeleteCountry,
  onSaveBrandSettings,
  onClose
}) => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const [showCountryManager, setShowCountryManager] = useState(false);
  const [showBrandSettings, setShowBrandSettings] = useState(false);

  const itemsPerPage = 5;

  // Set first country as default selected
  useEffect(() => {
    if (countries.length > 0 && !selectedCountry) {
      setSelectedCountry(countries[0]);
    }
  }, [countries, selectedCountry]);

  const handleAddNew = () => {
    setEditingCoupon(null);
    setShowForm(true);
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setShowForm(true);
  };

  const handleDelete = (couponId) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      onDeleteCoupon(selectedCountry.code, couponId);
    }
  };

  const handleSave = (coupon) => {
    onSaveCoupon(selectedCountry.code, coupon);
    setShowForm(false);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCoupon(null);
  };

  const filteredCoupons = selectedCountry
    ? (coupons[selectedCountry.code] || [])
        .filter(coupon => {
          const matchesSearch = searchTerm === '' || 
            coupon.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            coupon.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
            coupon.code.toLowerCase().includes(searchTerm.toLowerCase());
          
          if (filter === 'all') return matchesSearch;
          if (filter === 'highDiscount') {
            const discountValue = parseInt(coupon.discount) || 0;
            return matchesSearch && discountValue >= 30;
          }
          return matchesSearch;
        })
    : [];

  // Pagination
  const totalPages = Math.ceil(filteredCoupons.length / itemsPerPage);
  const paginatedCoupons = filteredCoupons.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const handleBackToMain = () => {
    setShowCountryManager(false);
    setShowBrandSettings(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-auto"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="mr-3 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="text-lg" />
            </button>
            <h2 className="text-xl font-bold text-gray-800">Coupon Control Panel</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <AnimatePresence mode="wait">
          {showCountryManager ? (
            <CountryManager
              key="countryManager"
              countries={countries}
              onAddCountry={onAddCountry}
              onEditCountry={onEditCountry}
              onDeleteCountry={onDeleteCountry}
              onBack={handleBackToMain}
            />
          ) : showBrandSettings ? (
            <BrandSettings
              key="brandSettings"
              brandSettings={brandSettings}
              onSaveBrandSettings={onSaveBrandSettings}
              onBack={handleBackToMain}
            />
          ) : showForm ? (
            <CouponForm
              key="form"
              countries={countries}
              selectedCountry={selectedCountry}
              coupon={editingCoupon}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          ) : (
            <motion.div
              key="panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col flex-1 overflow-hidden"
            >
              <div className="p-5 border-b">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <div className="relative">
                      <select
                        value={selectedCountry?.code || ''}
                        onChange={(e) => {
                          const country = countries.find(c => c.code === e.target.value);
                          setSelectedCountry(country);
                          setCurrentPage(1);
                        }}
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md border"
                      >
                        {countries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Search
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiSearch} className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        placeholder="Search coupons..."
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Filter
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiFilter} className="text-gray-400" />
                      </div>
                      <select
                        value={filter}
                        onChange={(e) => {
                          setFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        <option value="all">All Coupons</option>
                        <option value="highDiscount">High Discount (30%+)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-4">
                  <button
                    onClick={handleAddNew}
                    className="py-2 flex items-center justify-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiPlus} />
                    <span>Add New Coupon</span>
                  </button>

                  <button
                    onClick={() => setShowCountryManager(true)}
                    className="py-2 flex items-center justify-center space-x-2 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiGlobe} />
                    <span>Manage Countries</span>
                  </button>

                  <button
                    onClick={() => setShowBrandSettings(true)}
                    className="py-2 flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiBriefcase} />
                    <span>Brand Settings</span>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto">
                {paginatedCoupons.length > 0 ? (
                  <div className="p-5">
                    {paginatedCoupons.map((coupon) => (
                      <div
                        key={coupon.id}
                        className="mb-4 p-4 border rounded-xl hover:shadow-md transition-shadow bg-white flex flex-wrap md:flex-nowrap items-center gap-4"
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border bg-white p-1">
                          {coupon.logoImage && (
                            <img
                              src={coupon.logoImage}
                              alt={`${coupon.brand} logo`}
                              className="w-full h-full object-contain"
                            />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap justify-between items-start gap-2">
                            <div>
                              <h3 className="font-medium text-gray-900 truncate">
                                {coupon.brand}
                              </h3>
                              <p className="text-sm text-gray-500 truncate">
                                {coupon.title}
                              </p>
                            </div>
                            <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                              {coupon.discount}
                            </div>
                          </div>
                          <div className="mt-2 flex items-center">
                            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                              {coupon.code}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(coupon)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                            title="Edit coupon"
                          >
                            <SafeIcon icon={FiEdit2} />
                          </button>
                          <button
                            onClick={() => handleDelete(coupon.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                            title="Delete coupon"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full p-5">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <SafeIcon icon={FiSearch} className="text-gray-400 text-xl" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">No coupons found</h3>
                    <p className="text-gray-500 text-center">
                      {searchTerm
                        ? "Try changing your search term or filter"
                        : selectedCountry
                        ? `No coupons available for ${selectedCountry.name}`
                        : "Select a country to view coupons"}
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-4 border-t flex items-center justify-between">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-md ${
                      currentPage === 1
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <SafeIcon icon={FiChevronLeft} />
                  </button>

                  <div className="flex items-center space-x-2">
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i}
                        onClick={() => handlePageChange(i + 1)}
                        className={`w-8 h-8 flex items-center justify-center rounded-md ${
                          currentPage === i + 1
                            ? 'bg-blue-500 text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-md ${
                      currentPage === totalPages
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <SafeIcon icon={FiChevronRight} />
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default ControlPanel;